import { resolveProductLink } from "@/lib/product-link";

export type RefundMoney = {
  currency: string;
  amount: number;
};

export type RefundProduct = {
  orderNo: string;
  productNo: string;
  name: string;
  sku: string;
  image: string;
  total: string;
};

export type RefundCase = {
  id: string;
  caseNo?: string;
  orderNo: string;
  orderId?: string;
  amount: RefundMoney;
  reason: string;
  refundItemId?: string;
  reasonCode?: string;
  description?: string;
  quantity?: number;
  amountBreakdown?: { currency: string; item: number; domesticShipping: number; serviceFee: number; refundable: number };
  returnStatus?: "required" | "in_transit" | "received" | "waived";
  returnCarrier?: string;
  returnTrackingNo?: string;
  returnNote?: string;
  returnUpdatedAt?: string;
  method?: string;
  status: string;
  customerCancelled?: boolean;
  customerActionRequired?: boolean;
  supportTicketStatus?: string;
  attachmentCount?: number;
  createdAt: string;
  processedAt?: string;
  product: RefundProduct | null;
  orderSummary?: {
    orderType?: string;
    unitPrice?: RefundMoney;
    quantity?: number;
  };
};

export function waitingSummary(item: RefundCase) {
  if (item.customerActionRequired) return { waitingOn: "You", estimate: "Respond when ready" };
  if (item.status === "requested") return { waitingOn: "PRVBUY buyer team", estimate: "Review aimed within about 24 hours" };
  if (item.status === "approved") return { waitingOn: "PRVBUY finance", estimate: "Refund processing in progress" };
  return { waitingOn: "No action pending", estimate: "Case resolved" };
}

export function reasonLabel(item: RefundCase) {
  const labels: Record<string, string> = {
    dont_want_to_buy: "I Don't Want To Buy", information_error: "Information Error",
    seller_out_of_stock: "Seller Out Of Stock", unable_to_pay: "Unable To Pay", selected_wrong_product: "Selected Wrong Product", other_reasons: "Other Reasons",
  };
  return item.reasonCode ? labels[item.reasonCode] ?? item.reasonCode : item.reason.replace(/^Return:\s*/i, "");
}

export type RefundStatusEvent = {
  id: string;
  actorType: string;
  action: string;
  fromStatus?: string;
  toStatus: string;
  restoredOrderStatus?: string;
  note?: string;
  createdAt: string;
};

export type RefundAttachment = {
  id: string;
  uploadedByType: string;
  fileName: string;
  fileUrl: string;
  fileMime: string;
  fileSize: number;
  attachmentType: string;
  note?: string;
  createdAt: string;
};

export type RefundDetail = RefundCase & {
  order: {
    id?: string;
    orderNo: string;
    status: string;
    quantity?: number;
    createdAt?: string;
    updatedAt?: string;
    sellerName?: string;
    sellerUrl?: string;
    sourceUrl?: string;
    platform?: string;
  } | null;
  supportTicket: {
    id: string;
    subject: string;
    status: string;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  statusEvents?: RefundStatusEvent[];
  attachments?: RefundAttachment[];
  orderTimeline: Array<{ id: string; at: string; label: string; note?: string }>;
};

export type RefundRequestType = "return" | "legacy_exchange";
export type RefundGroup = "processing" | "completed" | "rejected";
export type RefundTab = "all" | RefundGroup;
export type RefundPrimaryAction = "provide_evidence" | "view_case";

export type RefundPresentation = {
  requestType: RefundRequestType;
  typeLabel: string;
  statusLabel: string;
  group: RefundGroup;
  nextAction: string;
  primaryAction: RefundPrimaryAction;
  canSupplement: boolean;
  canUploadAttachment: boolean;
  canCancel: boolean;
  isWalletComplete: boolean;
  isProviderUnavailable: boolean;
};

export type RefundFilters = {
  tab: RefundTab;
  keyword: string;
  requestType: "all" | RefundRequestType;
  status: "all" | "requested" | "approved" | "rejected" | "paid" | "cancelled" | "provider_unavailable";
  startDate: string;
  endDate: string;
};

export const REFUND_PAGE_SIZE = 10;

export const REFUND_TABS: Array<{ key: RefundTab; label: string }> = [
  { key: "all", label: "All" },
  { key: "processing", label: "Processing" },
  { key: "completed", label: "Refund Completed" },
  { key: "rejected", label: "Rejected" },
];

export function getRefundPresentation(item: RefundCase): RefundPresentation {
  const requestType: RefundRequestType = item.reason.startsWith("Exchange:") ? "legacy_exchange" : "return";
  const active = !item.customerCancelled && (item.status === "requested" || item.status === "approved");
  const isWalletComplete = item.status === "paid" && item.method === "wallet_credit";
  const isProviderUnavailable = item.status === "paid" && item.method === "original_payment";

  if (item.customerCancelled) {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Cancelled",
      group: "rejected",
      nextAction: "No customer action is available. Support can review the closed case.",
      primaryAction: "view_case",
      canSupplement: false,
      canUploadAttachment: false,
      canCancel: false,
      isWalletComplete: false,
      isProviderUnavailable: false,
    };
  }

  if (item.customerActionRequired && active) {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Action Required",
      group: "processing",
      nextAction: "Add the note or evidence requested by PRVBUY.",
      primaryAction: "provide_evidence",
      canSupplement: true,
      canUploadAttachment: true,
      canCancel: item.status === "requested",
      isWalletComplete: false,
      isProviderUnavailable: false,
    };
  }

  if (item.status === "requested") {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "In Review",
      group: "processing",
      nextAction: "Wait for PRVBUY to review the submitted case.",
      primaryAction: "view_case",
      canSupplement: true,
      canUploadAttachment: true,
      canCancel: true,
      isWalletComplete: false,
      isProviderUnavailable: false,
    };
  }

  if (item.status === "approved") {
    const returnPending = item.returnStatus === "required" || item.returnStatus === "in_transit";
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Refund Pending",
      group: "processing",
      nextAction: returnPending
        ? item.returnStatus === "in_transit"
          ? "The item is on its way back to the seller. Refund processing starts after receipt is confirmed."
          : "PRVBUY is arranging the approved return to the seller before refund processing."
        : "Finance processing is pending. Original-payment refund is unavailable.",
      primaryAction: "view_case",
      canSupplement: true,
      canUploadAttachment: true,
      canCancel: false,
      isWalletComplete: false,
      isProviderUnavailable: false,
    };
  }

  if (isWalletComplete) {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Refunded to Wallet",
      group: "completed",
      nextAction: "Wallet credit is posted. No further action is required.",
      primaryAction: "view_case",
      canSupplement: false,
      canUploadAttachment: false,
      canCancel: false,
      isWalletComplete: true,
      isProviderUnavailable: false,
    };
  }

  if (isProviderUnavailable) {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Provider Unavailable",
      group: "processing",
      nextAction: "No provider settlement is confirmed. Contact support for this legacy record.",
      primaryAction: "view_case",
      canSupplement: false,
      canUploadAttachment: false,
      canCancel: false,
      isWalletComplete: false,
      isProviderUnavailable: true,
    };
  }

  if (item.status === "rejected") {
    return {
      requestType,
      typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
      statusLabel: "Rejected",
      group: "rejected",
      nextAction: "Review the case decision or contact support.",
      primaryAction: "view_case",
      canSupplement: false,
      canUploadAttachment: false,
      canCancel: false,
      isWalletComplete: false,
      isProviderUnavailable: false,
    };
  }

  return {
    requestType,
    typeLabel: requestType === "legacy_exchange" ? "Legacy exchange" : "Return",
    statusLabel: "Status unavailable",
    group: "processing",
    nextAction: "Open the case for the latest server record.",
    primaryAction: "view_case",
    canSupplement: false,
    canUploadAttachment: false,
    canCancel: false,
    isWalletComplete: false,
    isProviderUnavailable: false,
  };
}

export function filterRefundCases(cases: RefundCase[], filters: RefundFilters) {
  const query = filters.keyword.trim().toLowerCase();

  return cases.filter((item) => {
    const presentation = getRefundPresentation(item);
    const createdDay = item.createdAt.slice(0, 10);
    const statusKey = item.customerCancelled
      ? "cancelled"
      : presentation.isProviderUnavailable
        ? "provider_unavailable"
        : item.status;
    const searchable = [
      item.caseNo ?? item.id,
      item.orderNo,
      item.product?.name ?? "",
      item.product?.sku ?? "",
      item.reason,
      presentation.statusLabel,
      presentation.typeLabel,
    ].join(" ").toLowerCase();

    return (filters.tab === "all" || presentation.group === filters.tab)
      && (filters.requestType === "all" || presentation.requestType === filters.requestType)
      && (filters.status === "all" || statusKey === filters.status)
      && (!query || searchable.includes(query))
      && (!filters.startDate || createdDay >= filters.startDate)
      && (!filters.endDate || createdDay <= filters.endDate);
  });
}

export function getRefundCounts(cases: RefundCase[]) {
  return cases.reduce<Record<RefundTab, number>>((counts, item) => {
    counts.all += 1;
    counts[getRefundPresentation(item).group] += 1;
    return counts;
  }, { all: 0, processing: 0, completed: 0, rejected: 0 });
}

export function paginateRefundCases(cases: RefundCase[], page: number, pageSize = REFUND_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(cases.length / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * pageSize;
  return {
    cases: cases.slice(start, start + pageSize),
    currentPage,
    totalPages,
    totalItems: cases.length,
    startItem: cases.length ? start + 1 : 0,
    endItem: Math.min(start + pageSize, cases.length),
  };
}

export function refundUnitPrice(item: RefundCase) {
  const quantity = item.quantity;
  const itemAmount = item.amountBreakdown?.item;
  if (quantity && quantity > 0 && itemAmount !== undefined && Number.isFinite(itemAmount)) {
    return { currency: item.amountBreakdown?.currency ?? item.amount.currency, amount: itemAmount / quantity };
  }
  return item.orderSummary?.unitPrice ?? null;
}

export function orderTypeLabel(item: RefundCase) {
  const summaryType = item.orderSummary?.orderType?.trim();
  if (summaryType) return summaryType;
  const platform = (item as RefundDetail).order?.platform?.trim();
  return platform || "Not recorded";
}

export function productLinkTarget(item: RefundCase) {
  const detail = item as RefundDetail;
  return resolveProductLink({
    productId: item.product?.productNo,
    sourceUrl: detail.order?.sourceUrl,
  });
}

export function refundDestination(item: RefundCase) {
  if (item.method === "wallet_credit" && item.status === "paid") return "PRVBUY Wallet — ledger posted";
  if (item.method === "wallet_credit") return "PRVBUY Wallet — not posted yet";
  if (item.method === "original_payment") return "Original payment — provider unavailable; settlement not confirmed";
  return "Not recorded by the server";
}

export function formatRefundMoney(value: RefundMoney) {
  return `${value.currency} ${value.amount.toFixed(2)}`;
}

export function formatRefundDate(value?: string) {
  if (!value) return "Unavailable";
  return value.replace("T", " ").slice(0, 19);
}

export function supportHref(item: RefundCase) {
  const params = new URLSearchParams({
    refundId: item.id,
    orderId: item.orderId ?? item.orderNo,
    topic: "after_sales",
  });
  if (item.product?.productNo) params.set("productId", item.product.productNo);
  return `/user-center/support?${params.toString()}`;
}

export function readableRefundError(code: string) {
  if (!/^[A-Z][A-Z0-9_]+$/.test(code)) return code;
  const messages: Record<string, string> = {
    USER_REQUIRED: "Sign in again to view this after-sales case.",
    USER_NOT_FOUND: "The current customer account could not be resolved.",
    REFUND_NOT_FOUND: "This case was not found for the current account.",
    REFUND_ALREADY_CLOSED: "This case is already closed. Refresh to view the current status.",
    REFUND_STATE_CONFLICT: "The case changed while this action was running. Refresh and review the current status.",
    REFUND_SUPPLEMENT_NOTE_REQUIRED: "Enter a supplement note before submitting.",
    REFUND_ATTACHMENT_FILE_REQUIRED: "Choose an image or PDF before uploading.",
    IDEMPOTENCY_KEY_REQUIRED: "The cancellation request could not be safely identified. Try again.",
  };
  return messages[code] ?? code.replaceAll("_", " ").toLowerCase();
}
